import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3106f248"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id"]
const _hoisted_2 = {
  class: "modal-box",
  method: "dialog"
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", null, null, -1))
const _hoisted_4 = { class: "grid grid-cols-2" }
const _hoisted_5 = { class: "flex justify-center items-center" }
const _hoisted_6 = { class: "flex justify-center items-center" }
const _hoisted_7 = { class: "upload-image-error text-xs text-red-500" }
const _hoisted_8 = { class: "modal-action" }

import UploadImage from "@/components/forms/UploadImage.vue";
import {
    fetchTransporterByUuid,
    updateTransporter,
    createTransporter,
} from "@/_services/transporter/transporter";
import { ref, watch, toRef, onMounted } from "vue";
import { ITransporterList } from "@/models/transporter/ITransporterList";
import { z } from "zod";
import { createZodPlugin } from "@formkit/zod";
import swal from "@/_helpers/swal";


export default /*#__PURE__*/_defineComponent({
  __name: 'TransporterFormModal',
  props: {
    transporterUuid: {
        type: String,
        required: false,
        default: "",
    },
    transporterFormModalId: {
        type: String,
        required: true,
    },
},
  emits: ["transporterCreated", "transporterUpdated"],
  setup(__props, { emit }) {

const props = __props;

const transporter = ref<ITransporterList>({
    name: "",
    isActive: true,
    vignette: null,
    uuid: "",
} as unknown as ITransporterList);


const uploadImgErrorMsg = ref<string>("");
const modalHTMLElement = ref<HTMLDialogElement | null>(null);
const modalDialogState = ref<"open" | "close">("close");
const transporterUuid = toRef(props, "transporterUuid");
const transporterFormModalId = toRef(props, "transporterFormModalId");
const modalTitle = transporterUuid.value
    ? ref<string>("Editer un transporteur")
    : ref<string>("Créer un transporteur");

onMounted(() => {
    modalHTMLElement.value = document.querySelector(
        `#${transporterFormModalId.value}`
    );
    if (modalHTMLElement.value) {
        observer.observe(modalHTMLElement.value, { attributes: true });
    }
});

const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
        if (mutation.attributeName === "open" && modalHTMLElement.value) {
            const isOpen = modalHTMLElement.value.open;
            const isclosed = !isOpen;
            modalDialogState.value = isOpen ? "open" : "close";
            if (transporterUuid.value && isclosed) {
                updateTransporterFromApi(transporterUuid.value as string);
            }
            if (
                (!transporterUuid.value && isclosed) ||
                (!transporterUuid.value && isOpen)
            ) {
                cleanForm();
            }

            break; // Exit the loop once the class attribute has been processed
        }
    }
});

const onImageUploaded = (image) => {
    transporter.value.vignette = {
        name: image.name ?? "",
        url: image.url,
        uuid: image.uuid,
    };
};

const onImageRemoved = () => {
    transporter.value.vignette = null;
};

const onUploadImgError = (errorMsg: string) => {
    uploadImgErrorMsg.value = errorMsg;
    console.dir(errorMsg);
};

const zodSchema = z.object({
    name: z.string()
        .trim()
        .nonempty("Le champs Nom du transporteur est obligatoire")
        .min(1, { message: "Le champs Nom du fournisseur est obligatoire" })
        .max(50, { message: "Le nom du fournisseur ne doit pas dépasser 50 caractères" })
        .regex(
            /^[a-zA-Z0-9_\sçé]*$/,
            "Le nom du transporteur ne doit pas contenir de caractères spéciaux"
        )
});

const [zodPlugin, submitHandler] = createZodPlugin(
    zodSchema,
    async (formData) => {
        try {
            closeModalDialog();
            const data = {
                name: formData.name,
                isActive: transporter.value.isActive,
                vignette: transporter.value.vignette
                    ? "/api/images/" + transporter.value.vignette.uuid
                    : null,
                uuid: transporter.value.uuid,
            };
            const isEdit = Boolean(transporterUuid.value);
            const title = isEdit
                ? "Êtes-vous sûr de vouloir éditer le transporteur"
                : "Êtes-vous sûr de vouloir créer le transporteur";
            const confirmText = isEdit ? "Oui, éditer" : "Oui, créer";
            await swal.confirm(title, "Annuler", confirmText).then(async (result) => {
                if (result.isConfirmed) {
                    if (isEdit) {
                        await updateTransporter(transporterUuid.value as string, data);
                        await swal.success("Transporteur édité avec succès", "");
                        emit("transporterUpdated");
                    } else {
                        await createTransporter(data);
                        await swal.info("Transporteur créé avec succès");
                        emit("transporterCreated");
                        cleanForm();
                    }
                }
            });
        } catch (err: any) {
            swal.error(err.errors[0]);
        }
    }
)

const cleanForm = () => {
    transporter.value = {
        name: "",
        isActive: true,
        vignette: null,
        uuid: "",
    } as unknown as ITransporterList;
};

const closeModalDialog = () => {
    if (modalHTMLElement.value) {
        modalHTMLElement.value.close();
    }
};

watch(
    () => transporterUuid.value,
    (newValue) => {
        if (newValue) {
            modalTitle.value = "Editer un transporteur";
            fetchTransporterByUuid(newValue).then((response) => {
                transporter.value = response.data;
            });
        } else {
            modalTitle.value = "Créer un transporteur";
        }
    }
);
watch(
    () => transporter.value.name,
    (newValue) => {
        if (newValue) {
            transporter.value.name = newValue.trimStart();
        }
    }
);

const updateTransporterFromApi = (uuid: string) => {
    fetchTransporterByUuid(uuid).then((response) => {
        transporter.value = response.data;
    });
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("dialog", {
    ref: "HTMLDialogElement",
    id: transporterFormModalId.value,
    class: "modal modal-middle sm:modal-middle"
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_FormKit, {
        type: "form",
        id: transporterFormModalId.value,
        onSubmit: _unref(submitHandler),
        plugins: [_unref(zodPlugin)],
        actions: false,
        "incomplete-message": "Désolé, tous les champs ne sont pas remplis correctement."
      }, {
        default: _withCtx(({ state: { valid } }) => [
          _createElementVNode("h2", null, _toDisplayString(_unref(modalTitle)), 1),
          _hoisted_3,
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(UploadImage, {
                "image-preview-url": transporter.value.vignette
                            ? transporter.value.vignette.url
                            : 'media/avatars/blank2.png',
                onImageUploaded: onImageUploaded,
                onImageRemoved: onImageRemoved,
                onError: _cache[0] || (_cache[0] = ($event: any) => (onUploadImgError($event)))
              }, null, 8, ["image-preview-url"])
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_FormKit, {
                modelValue: transporter.value.name,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((transporter.value.name) = $event)),
                type: "text",
                name: "name",
                label: "Nom du transporteur"
              }, null, 8, ["modelValue"])
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("span", null, _toDisplayString(uploadImgErrorMsg.value), 1)
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("button", {
              type: "button",
              class: "btn closeModalDialog text-white",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (closeModalDialog()))
            }, "Annuler"),
            _createVNode(_component_FormKit, {
              type: "submit",
              label: transporterUuid.value ? 'Modifier' : 'Créer',
              disabled: !valid
            }, null, 8, ["label", "disabled"])
          ])
        ]),
        _: 1
      }, 8, ["id", "onSubmit", "plugins"])
    ])
  ], 8, _hoisted_1))
}
}

})